import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import SubmitButton from 'components/ui/Button/SubmitButton';
import UnauthorizedPageTitle from 'components/ui/UnauthorizedPageTitle/UnauthorizedPageTitle';
import {forgotPasswordValidationSchema} from 'form/validation/forgotPassword';
import {useTriggerResetPassword} from 'api/users';
import {PATH} from 'constants/common';
import Form, {FormOnSubmit} from 'components/ui/Form/Form/Form';
import TextInput from 'components/ui/Form/TextInput/TextInput';
import IconSVG from 'components/ui/Icon/IconSVG';
import classNames from 'classnames';
import styles from './ForgotPasswordPage.module.scss';

export interface IForgotPasswordForm {
    email: string;
}

const ForgotPasswordPage = () => {
    const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
    const {executeRecaptcha} = useGoogleReCaptcha();

    const {mutate: resetPassword, isPending} = useTriggerResetPassword();

    const onSubmit: FormOnSubmit<IForgotPasswordForm> = async values => {
        const captcha = await executeRecaptcha!('login');
        resetPassword(
            {email: values.email, captcha},
            {
                onSettled: () => setIsEmailSent(true),
            },
        );
    };

    if (isEmailSent) {
        return (
            <div>
                <div style={{textAlign: 'center'}}>
                    <UnauthorizedPageTitle
                        pageTitle={<span>Well done!</span>}
                        titleTag="Well done!"
                        subtitle="We have sent you an email with the link to reset your password."
                    />
                    <p className="mb-4">Have a fantastic day!</p>
                    <Link
                        to={PATH.LOGIN}
                        className={classNames(
                            styles.submitBtn,
                            'btn btn-primary',
                        )}
                    >
                        Done
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <UnauthorizedPageTitle
                pageTitle={<span>Request a new password</span>}
                titleTag="Request a new password"
                subtitle="Please enter your e-mail address to reset your password."
            />
            <Form<IForgotPasswordForm>
                onSubmit={onSubmit}
                validationSchema={forgotPasswordValidationSchema}
            >
                <TextInput
                    label="Email"
                    type="email"
                    name="email"
                    placeholder="Email"
                    rounded={false}
                    size="large"
                />
                <SubmitButton
                    variant="primary"
                    className={styles.submitBtn}
                    isLoading={isPending}
                    disabled={isPending}
                    block
                >
                    Request New Password{' '}
                    <IconSVG name="upArrow" customDimensions className="ml-2" />
                </SubmitButton>
                <div className="mt-2">
                    Just a mistake?
                    <Link className="ml-1" to={PATH.LOGIN}>
                        Login here
                    </Link>
                </div>
            </Form>
        </>
    );
};

export default ForgotPasswordPage;
